import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../../../config";
import { mapNewCustomerCounts } from "./newCustomersSecuredHelpers";
import "./NewCustomersSecured.css";

// "New Customers Secured" widget — counts of newly secured customers for
// Week / Month-To-Date / Year-To-Date, sourced pre-computed from the
// NewCustomerCount dashboard endpoint. The backend scopes the response by the
// caller's role, so self vs all needs no client-side filtering.
//
// Role behaviour follows the shared dataScope contract (no role names hardcoded):
//   dataScope === "self" (Sales): the caller's own Week / MTD / YTD KPI cards.
//   dataScope === "all" (Sales Director / President): overall MTD + YTD totals,
//     then a per-sales-representative breakdown (Rep | MTD | YTD).

const ZERO_TOTALS = { week: 0, mtd: 0, ytd: 0 };
const EMPTY_RANGES = {
  week: { fromDate: "", toDate: "" },
  month: { fromDate: "", toDate: "" },
  year: { fromDate: "", toDate: "" },
};

// Data layer: one call to the pre-computed dashboard endpoint. `signal` aborts
// the in-flight request on unmount.
const fetchNewCustomerCounts = async ({ token, signal }) => {
  const res = await axios.get(
    `${API_URL}/Dashboard/api/Dashboard/NewCustomerCount`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  return mapNewCustomerCounts(res && res.data ? res.data : null);
};

const NewCustomersSecured = ({ auth: { auth }, dataScope = "self", userId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const isSelf = dataScope === "self";
  const token = auth && auth[0] ? auth[0].token : null;
  // let tmpUserId = userId;
  // if (auth && (auth[0]?.roleName === 'Sales' && auth[0]?.salesPersonId !== '')) {
  //   tmpUserId = auth[0]?.salesPersonId;
  // }
  // const salesPersonId = isSelf ? tmpUserId : null;

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchNewCustomerCounts({ token, signal: controller.signal })
      .then((result) => {
        if (cancelled) return;
        setSummary(result);
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        console.warn("Could not load New Customers Secured:", err.message);
        setError(true);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  const totals = summary ? summary.totals : ZERO_TOTALS;
  const reps = summary ? summary.reps : [];
  const ranges = summary ? summary.ranges : EMPTY_RANGES;

  // Sales (self) scopes the listing to the logged-in user; Director/President
  // ("all") drill into every rep's customers for the window.
  // const scopeExtra = isSelf ? { salesPersonId: userId } : {};
  const scopeExtra = isSelf ? {} : {};

  const openListing = (range, extra, itemSalesPersonId = 0) => {
    // if(!itemSalesPersonId) return true;
    const params = new URLSearchParams({
      fromDate: range.fromDate,
      toDate: range.toDate
    });
    // if(itemSalesPersonId){
    params.set('salesPersonId', itemSalesPersonId);
    // }
    // if(salesPersonId){
    //   params.set('salesPersonId', salesPersonId);
    // }
    navigate(`/new-customers-secured-info?${params.toString()}`);
  };

  // A KPI card; only clickable (drills into the listing) when its count > 0.
  const renderCard = (label, count, range, extra) => {
    const clickable = count > 0;
    return (
      <div
        className={`ncs-card${clickable ? " ncs-clickable" : ""}`}
      >
        <div className="ncs-card-value">{count}</div>
        <div className="ncs-card-label">{label}</div>
      </div>
    );
  };

  return (
    <div className="ncs-widget" style={{'width': '100%'}}>
      <h4 className="ncs-title" style={{'textAlign':'center'}}>New Customers Secured</h4>

      {loading ? (
        <div className="ncs-state">Loading…</div>
      ) : error ? (
        <div className="ncs-state ncs-error">Unable to load data.</div>
      ) :  ( //isSelf ? 
        <>{/*Sales: Week / MTD / YTD as KPI cards
        <div className="ncs-cards">
          {renderCard("Week To Date", totals.week, ranges.week, scopeExtra)}
          {renderCard("Month To Date", totals.mtd, ranges.month, scopeExtra)}
          {renderCard("Year To Date", totals.ytd, ranges.year, scopeExtra)}
        </div>.*/}
       {/* ) : ( */}
        <div className="ncs-overview">
          <div className="ncs-cards">
            {renderCard("Month To Date", totals.mtd, ranges.month, scopeExtra)}
            {renderCard("Year To Date", totals.ytd, ranges.year, scopeExtra)}
          </div>

          <table className="ncs-table">
            <thead>
              <tr>
                <th className="ncs-rowhead">Sales Person</th>
                <th>Month To Date</th>
                <th>Year To Date</th>
              </tr>
            </thead>
            <tbody>
              {reps.length > 0 ? (
                reps.map((rep, index) => {
                  let mtdClickable = rep.mtd > 0;
                  let ytdClickable = rep.ytd > 0;
                  // salesPersonId is the unique identifier; repName can repeat
                  // (e.g. several unnamed reps all show as "Unknown"). Fall back
                  // to the row index so keys stay unique when ids are missing.
                  return (<tr key={rep.salesPersonId ?? `rep-${index}`}>
                    <td className="ncs-rep">{rep.repName === 'Unknown' ? 'Unassigned' : rep.repName}</td>
                    <td className="ncs-num" style={{'cursor':'pointer'}} onClick={mtdClickable ? () => openListing(ranges.month, scopeExtra, rep.salesPersonId) : undefined}>{rep.mtd}</td>
                    <td className="ncs-num" style={{'cursor':'pointer'}} onClick={ytdClickable ? () => openListing(ranges.year, scopeExtra, rep.salesPersonId) : undefined}>{rep.ytd}</td>
                  </tr>)
                })
              ) : (
                <tr>
                  <td colSpan={3} className="ncs-empty">
                    No new customers secured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { NewCustomersSecured };

export default connect(mapStateToProps)(NewCustomersSecured);
